import { AuthUserState } from './dto/response';
import { setPermission } from '../redux/reducer/permissionSlice';
import store from '../redux/store';
import { MenuType } from './dto/module/user.type';
import { Tractor } from './dto/response/chat';

const CryptoJS = require('crypto-js');

const BASE_URL = process.env.REACT_APP_KHEETIGADI_API_BASE_URL

export function encryptText(plainText: any) {
    if (!plainText) return ''
    let key = CryptoJS.enc.Utf8.parse('22fd580b89e9c1db824253b34d8476fec9ec29d4dce249b980d0e7d34dce5b0c');
    let iv = CryptoJS.enc.Utf8.parse('9c2b9115327c834e3fa023377ad3f999');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    let encPassword = CryptoJS.AES.encrypt(plainText, key, config);
    return encPassword.ciphertext.toString(CryptoJS.enc.Base64);
}

export const decryptText = (text: any) => {
    if (!text) return ''
    let key = CryptoJS.enc.Utf8.parse('22fd580b89e9c1db824253b34d8476fec9ec29d4dce249b980d0e7d34dce5b0c');
    let iv = CryptoJS.enc.Utf8.parse('9c2b9115327c834e3fa023377ad3f999');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    try {
        let encPassword = CryptoJS.AES.decrypt(text, key, config);
        return encPassword.toString(CryptoJS.enc.Utf8);
    } catch (err) {
        try {
            return JSON.parse(text)
        } catch (err1) {
            return text || ''
        }
    }
}

export const isObject = (obj: any) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null

export const stableSort = <T>(array: readonly T[], comparator: (a: T, b: T) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export const getComparator = <Key extends keyof any>(order: any, orderBy: Key,): (
    a: { [key in Key]: any },
    b: { [key in Key]: any },
) => number => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export const isLoggedIn = (state: AuthUserState) => {
    const { Token, User, ExpiresAt } = state;
    const currentTime = Date.now();

    return !!User && !!Token;
}

export const getAllModuleUrls = (dispatch: any): string[] => {
    let moduleUrls: string[] = store.getState().permission?.moduleUrls ?? [];
    if (!moduleUrls || moduleUrls.length === 0) {
        try {
            const rolesPermissions = localStorage.getItem('roles_permissions');
            const mainPermissionsObject = rolesPermissions ? JSON.parse(decryptText(rolesPermissions)) : {};

            const collectUrls = (menus: any[]): string[] =>
                menus.flatMap((item: { url: string; sub_menus?: any[] }) => [
                    item.url,
                    ...(item.sub_menus ? collectUrls(item.sub_menus) : []),
                ]).filter((url: string) => url?.trim() !== "");
            moduleUrls = mainPermissionsObject.menus
                ? collectUrls(mainPermissionsObject.menus)
                : [];

            const permissionState = store.getState().permission ?? {};
            dispatch(setPermission({ ...permissionState, moduleUrls }));
        } catch (error) {
            console.error("Error fetching module URLs from local storage:", error);
            moduleUrls = [];
        }
    }
    return moduleUrls;
};

export const isAuthorizedPage = (dispatch: any, location: any): boolean => {
    const currentPath = location.pathname.split('?')[0];

    const moduleUrls = getAllModuleUrls(dispatch);
    return moduleUrls.includes(currentPath);
};

export const hasAccessToPath = (menus: any, path: string): MenuType | null => {
    if (!menus) return null;

    for (const menu of menus) {
        if (menu.url === path) {
            return menu;
        }

        if (menu.sub_menus && menu.sub_menus.length > 0 && hasAccessToPath(menu.sub_menus, path)) {
            return menu.sub_menus.find((menu: any) => menu.url === path) || null;
        }
    }

    return null;
};

export const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for very old browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const createSlug = (name: string): string => {
    if (!name) return '';
    return name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '') // remove special chars
        .replace(/-+/g, '-');
};

const createSafeSlug = (str?: string | null): string => {
    if (!str || str.trim() === '') return '';

    let result = str.trim();

    // Strictly follow the SAME order as PHP convertUrl()
    result = result.replace(/\?/g, '$$');     // 1. ? → $$
    result = result.replace(/-/g, '_');       // 2. - → _   (original hyphens become underscore)
    result = result.replace(/\s+/g, '-');     // 3. space → -
    result = result.replace(/\//g, ':');      // 4. / → :
    result = result.replace(/\./g, ';');      // 5. . → ;
    result = result.toLowerCase();            // 6. lowercase

    // Final cleanup: remove any invalid chars except letters, numbers, -, _
    result = result.replace(/[^a-z0-9_-]/g, '');

    // Collapse multiple - or _
    result = result.replace(/-+/g, '-');
    result = result.replace(/_+/g, '_');

    // Trim leading/trailing - and _
    result = result.replace(/^[-_]+|[-_]+$/g, '');

    return result;
};

export const getDetailUrl = (
    vehicle: Tractor,
    vehicle_type: string,
    slugName?: string[]
): string | null => {
    const base = BASE_URL;

    switch (vehicle_type) {
        case 'tractor_id': {
            const fullName = (vehicle.name || `${vehicle.brand_name || ''} ${vehicle.model_name || ''}`.trim());
            const slug = createSafeSlug(fullName);
            if (!slug) {
                console.error('Missing valid name for new tractor');
                return null;
            }
            return `${base}new-tractor-model/${slug}/en`;
        }

        case 'used_tractor_id': {
            const brandSlug = createSafeSlug(vehicle.brand_name);
            const modelSlug = createSafeSlug(vehicle.model_name);
            const citySlug = createSafeSlug(vehicle.city_name);
            const id = vehicle.id;

            if (!brandSlug || !modelSlug || !citySlug || !id) {
                console.error('Incomplete data for used tractor URL', {
                    brandSlug,
                    modelSlug,
                    citySlug,
                    id,
                });
                return null;
            }
            return `${base}buy-old-tractor/${brandSlug}/${modelSlug}/${citySlug}/${id}/en`;
        }

        case 'implement_id': {
            const fullName = (vehicle.name || `${vehicle.brand_name || ''} ${vehicle.model_name || ''}`.trim());
            const slug = createSafeSlug(fullName);
            if (!slug) {
                console.error('Missing valid name for implement');
                return null;
            }
            return `${base}new-implement-model/${slug}/en`;
        }

        case 'harvester_id': {
            const fullName = (vehicle.name || `${vehicle.brand_name || ''} ${vehicle.model_name || ''}`.trim());
            const slug = createSafeSlug(fullName);
            if (!slug) {
                console.error('Missing valid name for new harvester');
                return null;
            }
            return `${base}new-harvester-model/${slug}/en`;
        }

        case 'used_harvester_id': {
            const nameSlug = createSafeSlug(vehicle.name || vehicle.model_name || vehicle.brand_name);
            const citySlug = createSafeSlug(vehicle.city_name);
            const id = vehicle.id;

            if (!nameSlug || !citySlug || !id) {
                console.error('Incomplete data for used harvester URL', {
                    nameSlug,
                    citySlug,
                    id,
                });
                return null;
            }
            return `${base}used-harvester-model/${nameSlug}/${citySlug}/${id}/en`;
        }

        case 'tyre_id':
        case 'product_id':
            console.error(`No detail page for type: ${vehicle_type}`);
            return null;

        default:
            console.error(`Unsupported vehicle_type: ${vehicle_type}`);
            return null;
    }
};